import * as React from 'react';
import { FC, useEffect, useMemo, useState } from 'react';
import {
  ComponentsLayout,
  CustomButton,
  CustomInput,
  MedicineStock,
  theme,
  ToggleCard,
} from '@hdcorner/ui-library';
import { Box, Typography } from '@mui/material';
import PageLayout from '../../components/PageLayout';
import {
  useCreateMedicineStockMutation,
  useEditMedicineStockMutation,
  useGetMedicationByIdQuery,
  useGetMedicineStockQuery,
} from './queries/medicationQueries';
import { instructionCategories } from './constants';
import { RouteComponentProps } from 'react-router';
import { useNotifyUserMutation } from '../../redux/queries/queries';
import useAlert from '../../hooks/useAlert';
import { useTranslation } from 'react-i18next';

interface Props
  extends RouteComponentProps<{
    userId: string;
    medicationId: string;
  }> {}
const MedicationInstructions: FC<Props> = ({
  match: {
    params: { userId, medicationId },
  },
}) => {
  const { t } = useTranslation();
  const { presentError } = useAlert();

  const [instructions, setInstructions] = useState<string>('');
  const [instructionsCategory, setInstructionsCategory] = useState<
    'before_meal' | 'after_meal' | 'with_meal' | 'no_instructions'
  >('no_instructions');

  const [notify] = useNotifyUserMutation();
  const [editMedicineStock] = useEditMedicineStockMutation();
  const [createMedicineStock] = useCreateMedicineStockMutation();

  const { data: medicationRes, error: errorMedication } = useGetMedicationByIdQuery(
    { medicationId, userId },
    {
      skip: !medicationId,
    },
  );

  const medication = useMemo(() => {
    if (!medicationRes || medicationRes.length === 0) return null;
    return medicationRes[0];
  }, [medicationRes]);

  const { data: medicineStockRes, error: errorMedicineStock } = useGetMedicineStockQuery(
    { medicineId: medication ? medication.medicine._id : '', userId },
    {
      skip: !medication || !medication.medicine._id,
    },
  );

  useEffect(() => {
    if (errorMedication || errorMedicineStock)
      presentError(t('errors.medication.errorFetchMeds'));
  }, [errorMedication, errorMedicineStock]);

  const medicineStock = useMemo<MedicineStock | null>(() => {
    if (!medicineStockRes || medicineStockRes.length === 0) return null;
    return medicineStockRes[0];
  }, [medicineStockRes]);

  useEffect(() => {
    if (medicineStock) {
      setInstructionsCategory(medicineStock.instructionsCategory || 'no_instructions');
      setInstructions(medicineStock.instructions || '');
      // if (medicineStock.instructionsCategory === 'no_instructions') {
      //   setInstructionsCategory('no_instructions');
      //   setInstructions('');
      // } else {
      //   setInstructionsCategory(medicineStock.instructionsCategory || 'no_instructions');
      //   if (medicineStock.instructions) {
      //     setInstructions(medicineStock.instructions);
      //   } else {
      //     setInstructions('');
      //   }
      // }
    }
  }, [medicineStock]);

  const updateInstructions = () => {
    if (medicineStock) {
      editMedicineStock({
        instructions,
        instructionsCategory,
        _id: medicineStock._id,
      })
        .unwrap()
        .then(() => {
          notify({
            userId,
            payload: {
              title: t('medication.notifications.editInstr'),
              body: t('medication.notifications.editInstrBody'),
            },
          });
        })
        .catch(() => {
          presentError(t('errors.generic.error01'));
        });
    } else {
      if (!medication) return;
      createMedicineStock({
        userId,
        instructions,
        instructionsCategory,
        medicine: medication.medicine._id,
      })
        .unwrap()
        .then(() => {
          notify({
            userId,
            payload: {
              title: t('medication.notifications.addInstr'),
              body: t('medication.notifications.addInstrBody'),
            },
          });
        })
        .catch(() => {
          presentError(t('errors.generic.error01'));
        });
    }
  };

  const handleChangeCategory = (value: string) => {
    const castedValue = value as
      | 'before_meal'
      | 'after_meal'
      | 'with_meal'
      | 'no_instructions';
    setInstructionsCategory(castedValue);
  };

  return (
    <PageLayout
      headerColor={theme.palette.primary.main}
      headerTitle={t('buttons.navigation.medInstructions')}
      defaultHref={`/dashboard/users/${userId}/medication`}
    >
      <ComponentsLayout>
        <Box display={'flex'} flexDirection={'column'} gap={2}>
          <Box>
            {instructionCategories.map((category, index) => (
              <ToggleCard
                key={index}
                value={category.value}
                textTransform={'none'}
                label={t(category.label)}
                setSelected={handleChangeCategory}
                backgroundColor={theme.palette.secondary.light}
                selected={instructionsCategory === category.value}
              />
            ))}
          </Box>
          <Box>
            <Typography
              variant={'subtitle2'}
              color={theme.palette.primary.main}
              marginBottom={theme.spacing(1)}
              fontWeight={theme.typography.fontWeightRegular}
            >
              {t('medication.text.customInstructions')}
            </Typography>
            <CustomInput
              fullWidth
              value={instructions}
              alternativeBackground
              handleChange={setInstructions}
              placeholder={`${t('medication.text.enterCustom')}`}
            />
          </Box>
          <CustomButton
            fullWidth
            disableElevation
            color={'primary'}
            variant={'contained'}
            onClick={updateInstructions}
          >
            {t('buttons.update')}
          </CustomButton>
        </Box>
      </ComponentsLayout>
    </PageLayout>
  );
};

export default MedicationInstructions;
